import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FileArrowUp, Folder, PencilSimple, Plus, Trash } from "@phosphor-icons/react";

function nextFolderId() {
  return `folder_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
}

export default function JobWizardDocumentFolders({ folders, onFoldersChange, files, onFilesChange }) {
  const [folderName, setFolderName] = useState("");
  const [activeFolderId, setActiveFolderId] = useState(folders[0]?.id || "general");
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState("");
  const activeFolder = folders.find((folder) => folder.id === activeFolderId) || folders[0];
  const addFolder = () => {
    const name = folderName.trim();
    if (!name || folders.some((folder) => folder.name.toLowerCase() === name.toLowerCase())) return;
    const folder = { id: nextFolderId(), name };
    onFoldersChange([...folders, folder]);
    setActiveFolderId(folder.id);
    setFolderName("");
  };
  const renameFolder = (folderId) => {
    const name = editingName.trim();
    if (!name) return;
    onFoldersChange(folders.map((folder) => (folder.id === folderId ? { ...folder, name } : folder)));
    onFilesChange(
      files.map((file) => (file.folder_id === folderId ? { ...file, folder_name: name, category: name } : file)),
    );
    setEditingId(null);
  };
  const deleteFolder = (folderId) => {
    const remaining = folders.filter((folder) => folder.id !== folderId);
    onFoldersChange(remaining);
    onFilesChange(
      files.map((file) =>
        file.folder_id === folderId
          ? { ...file, folder_id: "general", folder_name: "General", category: "General" }
          : file,
      ),
    );
    if (activeFolderId === folderId) setActiveFolderId(remaining[0]?.id || "general");
  };
  const addFiles = (event) => {
    const target = activeFolder || { id: "general", name: "General" };
    const selected = Array.from(event.target.files).map((file) => ({
      file,
      category: target.name,
      folder_id: target.id,
      folder_name: target.name,
    }));
    onFilesChange([...files, ...selected]);
    event.target.value = "";
  };

  return (
    <section className="space-y-4" data-testid="job-wizard-document-folders">
      <div className="border border-slate-200 bg-white p-4">
        <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-600">
              Job document folders
            </div>
            <p className="mt-1 text-xs text-slate-500">
              Create folders for this job only. Removing a folder keeps its selected files under General.
            </p>
          </div>
          <div className="flex gap-2">
            <Input
              value={folderName}
              onChange={(event) => setFolderName(event.target.value)}
              onKeyDown={(event) => event.key === "Enter" && addFolder()}
              placeholder="New folder name"
              className="h-8 w-48 border-slate-200 text-xs"
              data-testid="job-folder-name-input"
            />
            <Button
              type="button"
              onClick={addFolder}
              disabled={!folderName.trim()}
              className="h-8 bg-emerald-600 text-xs text-white hover:bg-emerald-700"
              data-testid="job-folder-add"
            >
              <Plus size={14} /> Add
            </Button>
          </div>
        </div>
        <div className="space-y-2" data-testid="job-folder-list">
          {folders.map((folder) => (
            <div
              key={folder.id}
              className={`flex items-center gap-2 border px-3 py-2 ${
                activeFolderId === folder.id ? "border-emerald-400 bg-emerald-50" : "border-slate-200"
              }`}
              data-testid={`job-folder-${folder.id}`}
            >
              {editingId === folder.id ? (
                <div className="flex min-w-0 flex-1 items-center gap-2">
                  <Folder size={16} className="shrink-0 text-emerald-700" />
                  <Input
                    value={editingName}
                    onChange={(event) => setEditingName(event.target.value)}
                    onKeyDown={(event) => event.key === "Enter" && renameFolder(folder.id)}
                    className="h-7 max-w-xs border-slate-300 text-xs"
                    data-testid={`job-folder-edit-input-${folder.id}`}
                  />
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setActiveFolderId(folder.id)}
                  className="flex min-w-0 flex-1 items-center gap-2 text-left text-sm font-semibold text-slate-700"
                  data-testid={`job-folder-view-${folder.id}`}
                >
                  <Folder size={16} className="shrink-0 text-emerald-700" />
                  <span className="truncate">{folder.name}</span>
                </button>
              )}
              {editingId === folder.id ? (
                <button
                  type="button"
                  onClick={() => renameFolder(folder.id)}
                  className="text-xs font-bold text-emerald-700"
                  data-testid={`job-folder-save-${folder.id}`}
                >
                  Save
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    setEditingId(folder.id);
                    setEditingName(folder.name);
                  }}
                  className="p-1 text-slate-400 hover:text-emerald-700"
                  data-testid={`job-folder-edit-${folder.id}`}
                  aria-label={`Rename ${folder.name}`}
                >
                  <PencilSimple size={15} />
                </button>
              )}
              {folder.id !== "general" && (
                <button
                  type="button"
                  onClick={() => deleteFolder(folder.id)}
                  className="p-1 text-slate-400 hover:text-rose-600"
                  data-testid={`job-folder-delete-${folder.id}`}
                  aria-label={`Delete ${folder.name}`}
                >
                  <Trash size={15} />
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
      <label className="flex min-h-32 cursor-pointer flex-col items-center justify-center border border-dashed border-emerald-300 bg-emerald-50/30 p-6 text-center">
        <FileArrowUp size={28} className="text-emerald-600" />
        <span className="mt-2 text-sm font-semibold text-emerald-800">
          Add documents to {activeFolder?.name || "General"}
        </span>
        <span className="mt-1 text-xs text-slate-500">Maximum 50 MB each</span>
        <input
          type="file"
          multiple
          className="hidden"
          onChange={addFiles}
          data-testid="job-documents-input"
        />
      </label>
    </section>
  );
}